import React from 'react';
import { DrawerList, DrawerSection, DrawerSectionsWrapper, ResponsiveDrawer } from './ResponsiveDrawer';
import Text from 'antd/lib/typography/Text';
import { workflowsAndBranchesFromBuildDistroBucketRules } from '../../../helpers';
import { AppComponentProps } from '../../../typescriptHelpers';
import { antGrey } from '../../styles/globalStyles';
import Space from 'antd/lib/space';
import Avatar from 'antd/lib/avatar';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { selectIntegrationForCapability } from '../../../selectors/selectors';
import { IntegrationCapability, BuildDistributionBucket } from '../../../types';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '../../../timeHelpers';
import { MobileAddEditBucketMembers } from '../AddEditBucketMembers';
import { EditOutlined } from '@ant-design/icons';
import PermissionedActionButton from '../../HOCs/PermissionedActionButton';
import { WorkflowAndBranchTag } from '../CommonComponents';
import { RunwayUserAction } from '../../../helpers/aclHelpers';
import { useApp } from '@/utils/hooks/helpers';

export interface BuildBucketDetailsDrawerProps extends AppComponentProps {
    open: boolean;
    bucket: BuildDistributionBucket;
    title: string;
    onClose: () => void;
    afterOpenChange?: (boolean) => void;
}

export const BuildBucketDetailsDrawer: React.FC<BuildBucketDetailsDrawerProps> = (props) => {
    const { title, open, onClose, appId, orgId, bucket, afterOpenChange } = props;

    const [isEditingMembers, setIsEditingMembers] = React.useState(false);

    const buildSystemIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.BuildSystem),
    );

    const app = useApp(props);
    const firstLetter = app.appSettings.appName.charAt(0);
    return (
        <ResponsiveDrawer
            open={open}
            onClose={onClose}
            afterOpenChange={afterOpenChange}
            header={
                <Space size={20}>
                    <Avatar
                        shape={'square'}
                        size={36}
                        src={app.appSettings.appImageUrl}
                        style={{
                            flexShrink: 0,
                            boxShadow: '0px 1.5px 3px rgba(0, 0, 0, 0.12)',
                        }}
                    >
                        {firstLetter}
                    </Avatar>
                    <Text
                        style={{
                            fontSize: 16,
                            color: antGrey.grey8,
                        }}
                    >
                        {title}
                    </Text>
                </Space>
            }
            content={
                <DrawerSectionsWrapper>
                    <DrawerSection title="Bucket settings">
                        <DrawerList
                            items={[
                                {
                                    title: (
                                        <Text
                                            ellipsis
                                            style={{
                                                color: antGrey.grey7,
                                                fontSize: 14,
                                                alignSelf: 'start',
                                                marginBottom: 12,
                                            }}
                                        >
                                            {title}
                                        </Text>
                                    ),
                                    description: (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-end',
                                                gap: 8,
                                                flexGrow: 1,
                                            }}
                                        >
                                            {workflowsAndBranchesFromBuildDistroBucketRules(bucket?.rules)?.map(
                                                (workflowAndBranch) => (
                                                    <WorkflowAndBranchTag
                                                        key={workflowAndBranch.workflowName + workflowAndBranch.branch}
                                                        workflowAndBranch={workflowAndBranch}
                                                        buildSystemIntegrationType={buildSystemIntegration?.id}
                                                    />
                                                ),
                                            )}
                                        </div>
                                    ),
                                },
                            ]}
                        />
                        <DrawerList
                            items={[
                                {
                                    title: 'Created',
                                    description: dayjs(bucket.createdAt).format(DatetimeFormat.ShortMonthDateYear),
                                },
                            ]}
                        />
                    </DrawerSection>

                    <DrawerSection
                        title="Members"
                        extra={
                            isEditingMembers ?
                                <PermissionedActionButton type="link" onClick={() => setIsEditingMembers(false)}>
                                    Done
                                </PermissionedActionButton>
                            :   <PermissionedActionButton
                                    actionType={RunwayUserAction.UABucketUpdateBucketMembers}
                                    bucketId={bucket?.id}
                                    type="link"
                                    onClick={() => setIsEditingMembers(true)}
                                    icon={<EditOutlined />}
                                >
                                    Edit
                                </PermissionedActionButton>
                        }
                    >
                        <MobileAddEditBucketMembers
                            members={bucket.members ?? []}
                            excludedMembers={[]}
                            appId={appId}
                            isEditing={isEditingMembers}
                            orgId={orgId}
                            bucketId={bucket.id}
                        />
                    </DrawerSection>
                </DrawerSectionsWrapper>
            }
        />
    );
};
